import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Database, Search, Download, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { AppHeader } from "../components/elysium/AppHeader";
import { Reveal } from "../components/elysium/primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DELETABLE = new Set(["soulkeys", "stories", "permissions", "access_log", "sk_messages"]);

const TABS = [
  { key: "users", label: "Users" },
  { key: "soulkeys", label: "Soul-Keys" },
  { key: "stories", label: "Stories" },
  { key: "permissions", label: "Permissions" },
  { key: "access_log", label: "Access Log" },
  { key: "sk_messages", label: "Messages" },
];

const cell = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v);
  return s.length > 80 ? s.slice(0, 80) + "…" : s;
};

const toCsv = (columns, rows) => {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const header = columns.map(esc).join(",");
  const body = rows.map((r) => columns.map((c) => esc(r[c])).join(",")).join("\n");
  return `${header}\n${body}`;
};

export const Admin = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("users");
  const [query, setQuery] = useState("");
  const [err, setErr] = useState("");

  const loadData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/data`);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-ely-bg"><Loader2 className="animate-spin text-ely-primary" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;

  const allRows = data ? data[tab] : [];
  const columns = allRows.length ? Object.keys(allRows[0]) : [];
  const q = query.trim().toLowerCase();
  const rows = q
    ? allRows.filter((r) => Object.values(r).some((v) => String(typeof v === "object" ? JSON.stringify(v) : v).toLowerCase().includes(q)))
    : allRows;

  const exportCsv = () => {
    const csv = toCsv(columns, rows);
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `elysium-${tab}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this record permanently? (Deleting a Soul-Key also removes its stories, permissions, messages and logs.)")) return;
    try {
      await axios.delete(`${API}/admin/${tab}/${id}`);
      await loadData();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const canDelete = DELETABLE.has(tab);

  return (
    <div className="min-h-screen bg-ely-bg">
      <div className="grain" aria-hidden="true" />
      <AppHeader />
      <main className="mx-auto max-w-6xl px-6 py-14 md:px-10" data-testid="admin-page">
        <Reveal>
          <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-ely-primary">
            <Database size={13} /> Admin · Database viewer
          </span>
          <h1 className="mt-5 font-serif text-4xl font-light tracking-tight text-ely-text md:text-6xl">All your data.</h1>
        </Reveal>

        {err && <p className="mt-6 font-sans text-red-400" data-testid="admin-error">{err}</p>}

        {!data ? (
          <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-ely-primary" /></div>
        ) : (
          <>
            {/* counts */}
            <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {TABS.map((t) => (
                <div key={t.key} className="rounded-lg border border-ely-border/60 bg-ely-surface/40 p-4">
                  <p className="font-serif text-3xl font-light text-ely-primary">{data.counts[t.key] ?? 0}</p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-ely-muted">{t.label}</p>
                </div>
              ))}
            </div>

            {/* tabs */}
            <div className="mt-10 flex flex-wrap gap-2 border-b border-ely-border/50 pb-3">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => { setTab(t.key); setQuery(""); }}
                  data-testid={`admin-tab-${t.key}`}
                  className={`rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-wider transition-colors ${tab === t.key ? "bg-ely-primary text-ely-bg" : "border border-ely-border text-ely-muted hover:border-ely-primary hover:text-ely-primary"}`}
                >
                  {t.label} ({data.counts[t.key] ?? 0})
                </button>
              ))}
            </div>

            {/* toolbar: search + export */}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-1 items-center gap-2 rounded-full border border-ely-border bg-ely-surface/40 px-4 py-2">
                <Search size={14} className="text-ely-muted" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Search ${tab}…`}
                  data-testid="admin-search"
                  className="w-full bg-transparent font-sans text-sm text-ely-text placeholder:text-ely-muted/60 outline-none"
                />
                {query && <span className="font-mono text-[10px] text-ely-muted">{rows.length} match</span>}
              </div>
              <button
                onClick={exportCsv}
                disabled={!rows.length}
                data-testid="admin-export"
                className="flex items-center justify-center gap-2 rounded-full border border-ely-border px-4 py-2 font-mono text-[11px] uppercase tracking-wider text-ely-muted transition-colors hover:border-ely-primary hover:text-ely-primary disabled:opacity-40"
              >
                <Download size={13} /> Export CSV
              </button>
            </div>

            {/* table */}
            <div className="mt-4 overflow-x-auto rounded-xl border border-ely-border/60" data-testid="admin-table">
              {rows.length ? (
                <table className="w-full min-w-[640px] text-left">
                  <thead>
                    <tr className="border-b border-ely-border/60 bg-ely-surface/60">
                      {columns.map((c) => (
                        <th key={c} className="whitespace-nowrap px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-ely-primary">{c}</th>
                      ))}
                      {canDelete && <th className="px-4 py-3" />}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-b border-ely-border/30 last:border-0 hover:bg-ely-surface/30">
                        {columns.map((c) => (
                          <td key={c} className="px-4 py-3 font-sans text-sm font-light text-ely-text/80" title={typeof r[c] === "object" ? JSON.stringify(r[c]) : String(r[c] ?? "")}>{cell(r[c])}</td>
                        ))}
                        {canDelete && (
                          <td className="px-4 py-3">
                            <button onClick={() => del(r.id)} data-testid={`admin-delete-${r.id}`} className="text-ely-muted transition-colors hover:text-red-400" title="Delete">
                              <Trash2 size={15} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="p-6 font-sans text-sm font-light text-ely-muted">{query ? "No records match your search." : "No records in this collection yet."}</p>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};
